import React, { useState, useEffect } from "react";
import { observer } from "mobx-react";
import Utils from "Utils/utils";
import InputTextBox from "Components/common/InputTextBox";
import Button from "Components/common/Button";
import { toasterState } from "Components/common/toaster";
import { accountsState } from "./AccountsState";

const CreateNewList = observer((props = {}) => {
  const {
    listName = "",
    loader = false,
    err = "",
    showCreateList = false,
    setShowCreateList = () => {},
    changeAction = () => {},
    setListName = () => {},
    action = () => {}
  } = props || {};

  const toggleListCreation = () => {
    setShowCreateList(!showCreateList);
    showCreateList && setListName("");
  };

  const createListInputObj = {
    name: "createNewMoveAccountList",
    id: "createNewMoveAccountList",
    type: "text",
    value: listName,
    change: changeAction,
    err,
    placeHolder: "Enter the list name",
    focus: true,
    autoFocus: true,
    cbk: event => (Utils.isSubmitKeyAction(event) && action()) || {}
  };

  const createListBtn = {
    id: "addNewMoveAccountListBtn",
    name: "addNewMoveAccountListBtn",
    btnText: "",
    type: "button",
    loader,
    icon: true,
    iconName: "playlist_add",
    className: `addNewListBtn ${listName?.trim()?.length ? "" : "disabled"} ${
      loader ? "loading" : ""
    }`,
    click: action,
    iconCbk: () => {}
  };

  return (
    <div className="createNewListButton">
      <div className="createListBtn" onClick={toggleListCreation}>
        <i className="material-icons-outlined add">control_point</i>
        <span className="text">Create a new list</span>
      </div>
      {showCreateList && (
        <div className="wrapInputAction">
          <InputTextBox {...createListInputObj} />
          <Button {...createListBtn} />
        </div>
      )}
    </div>
  );
});

export const MoveAccountsToList = observer((props = {}) => {
  const {
    accountIds = [],
    loading = false,
    trigger = 0,
    onMoveToList = () => {}
  } = props || {};

  const [showCreateList, setShowCreateList] = useState(false);
  const [listName, setListName] = useState("");
  const [err, setErr] = useState("");
  const [selectedListId, setSelectedListId] = useState("");
  const [showList, setShowList] = useState(false);
  const [loader, setLoader] = useState(false);
  const [newListLoader, setNewListLoader] = useState(false);
  const [searchText, setSearchText] = useState("");

  const sourceListId = accountsState?.activeAccountListId;

  const getSelectableLists = () => {
    return (accountsState.accountLists || []).filter(
      item => item?.id !== sourceListId
    );
  };

  const [options, setOptions] = useState(getSelectableLists());

  useEffect(() => {
    if (trigger > 0) {
      if (!loading && accountIds?.length > 0) {
        setShowList(true);
      }
    }
  }, [trigger]);

  const resetToInitial = () => {
    setShowList(false);
    setSearchText("");
    setSelectedListId("");
    setListName("");
    setErr("");
  };

  const closePopup = () => {
    !loader && !newListLoader && resetToInitial();
  };

  const changeListNameAction = (id = "", value = "") => {
    err && setErr("");
    setListName(value);
    setSearchText("");
    setSelectedListId("");
  };

  const handleSearchText = (id, value) => {
    setSearchText(value);
    setSelectedListId("");
    setListName("");
    err && setErr("");
    const selectable = getSelectableLists();
    if (value) {
      setOptions(
        selectable.filter(item =>
          (item?.name?.toLowerCase()?.trim() || "").includes(
            value?.toLowerCase()
          )
        )
      );
    } else {
      setOptions(selectable);
    }
  };

  const searchInputObj = {
    name: "searchMoveAccountListInput",
    id: "searchMoveAccountListInput",
    type: "text",
    value: searchText,
    change: handleSearchText,
    placeHolder: "Search lists",
    focus: true
  };

  const handleChange = (id = "") => {
    setSelectedListId(id);
    setShowCreateList(false);
    setListName("");
    err && setErr("");
  };

  const moveToListBtn = {
    id: "moveAccountToSelectedListBtn",
    name: "moveAccountToSelectedList",
    btnText: "Move to List",
    type: "button",
    loader,
    className: `addContactToSelectedListBtn ${
      selectedListId ? "" : "disabled"
    } ${loader ? "loading" : ""}`,
    click: () => {
      if (!selectedListId || !sourceListId) return;
      setLoader(true);
      onMoveToList(selectedListId, () => {
        setLoader(false);
        closePopup();
      });
    }
  };

  const createNewListAction = async () => {
    if (!newListLoader && listName?.trim()?.length) {
      try {
        setNewListLoader(true);
        const response = await accountsState.createAccountList(
          listName.trim()
        );
        if (response?.data?.error) {
          setErr("List name already exists.");
        } else if (response?.data?.id) {
          await accountsState.fetchAccountLists();
          setShowCreateList(false);
          setListName("");
          setSelectedListId(response.data.id);
          toasterState.setToastMsg("List created successfully", "success");
        } else {
          toasterState.setToastMsg(
            "Failed to create list. Please try again later",
            "failure"
          );
        }
        setNewListLoader(false);
      } catch (e) {
        setNewListLoader(false);
        console.error(e);
      }
    }
  };

  useEffect(() => {
    return () => {
      resetToInitial();
    };
  }, []);

  useEffect(() => {
    setOptions(getSelectableLists());
  }, [accountsState.accountLists, sourceListId]);

  return (
    <div className="wrapAddTolistBtnPopup">
      {showList && (
        <div className="addListPopup">
          <div className="card animated customzoomIn">
            <div className="cardHeader">
              <div className="title">Move to List</div>
              <i className="material-icons close" onClick={closePopup}>
                close
              </i>
            </div>
            <div
              className={`cardBody ${newListLoader || loader ? "loading" : ""}`}
            >
              <CreateNewList
                showCreateList={showCreateList}
                setShowCreateList={setShowCreateList}
                listName={listName}
                err={err}
                loader={newListLoader}
                setListName={setListName}
                setErr={setErr}
                changeAction={changeListNameAction}
                setLoader={setNewListLoader}
                action={createNewListAction}
              />
              <div className="selectListDropdown">
                <div className="listHeading">Select a list to move accounts</div>
                <div className="selectOptions">
                  <div className="searchInputBox">
                    <i className="material-icons search">search</i>
                    <InputTextBox {...searchInputObj} />
                  </div>
                  <div className="optionList">
                    {options?.length > 0 ? (
                      options?.map((item, index) => {
                        const { count = 0, createTs = "", name = "", id = "" } =
                          item || {};
                        return (
                          <div
                            className={`options ${
                              selectedListId === id ? "disableSelect" : ""
                            }`}
                            key={`moveAccountListId_${id}${index}`}
                          >
                            <div className="radioBtnCont">
                              <div className="radioButtonCont">
                                <input
                                  type="radio"
                                  name="moveToAccountListRadioCheckbox"
                                  id={id}
                                  onChange={() => handleChange(id)}
                                  checked={selectedListId === id}
                                  className="radioBtn"
                                />
                                <span className="contentClass" />
                              </div>
                            </div>
                            <label className="listDetails" htmlFor={id}>
                              <div
                                className={`listInfo ${
                                  !createTs ? "alignSelfCenter" : ""
                                }`}
                              >
                                <span className="name">{name}</span>
                                {createTs && (
                                  <span className="createdInfo">
                                    Created on {Utils.formatDate(createTs)}
                                  </span>
                                )}
                              </div>
                              <div className="listContactsCount">
                                <span className="count">{count}</span>
                                <span className="text">
                                  Account{count > 1 ? "s" : ""}
                                </span>
                              </div>
                            </label>
                          </div>
                        );
                      })
                    ) : (
                      <div className="noListFound">No list found</div>
                    )}
                  </div>
                </div>
              </div>
              <Button {...moveToListBtn} />
            </div>
          </div>
          <div className="bgOverlay" />
        </div>
      )}
    </div>
  );
});

export default MoveAccountsToList;
